import React from "react";
import { connect } from "react-redux";

import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";
import { AppDispatch, IAppState } from "../core/state/Store";
import { Side } from "../core/types/system/Layout";
import Text from "../core/components/text/Text";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import { unwrappedDispatch } from "../core/foundation/Dispatching";
import Tabs from "../core/components/tabs/Tabs";
import TabContainer from "../core/components/tabs/TabContainer";
import PageSpecification from "../core/types/system/PageSpecification";
import AccessLog from "../core/types/system/AccessLog";
import {
    getAccessLogPage,
    setAccessLogPageAndSize,
} from "../core/state/system/AccessLog";
import {
    addInternalSpecification,
    getLogPage,
    LogPageSpecification,
    setLogPageAndSize,
} from "../core/state/system/Logs";
import LogEntry from "../core/types/system/LogEntry";

import "./Logs.scss";

interface RouteLogsProps {
    accessLogs: AccessLog[];
    accessLogsPageSpec: PageSpecification;
    logs: LogEntry[];
    logsPageSpec: PageSpecification;

    setPageLoading: (loading: boolean) => void;
    getAccessLogPage: () => Promise<void>;
    setAccessLogPageAndSize: (spec: PageSpecification) => void;
    getLogPage: (type: string) => Promise<void>;
    setLogPageAndSize: (specification: LogPageSpecification) => void;
    addInternalSpecification: (specification: LogPageSpecification) => void;
}

interface RouteLogsState {}

class RouteLogs extends React.Component<RouteLogsProps, RouteLogsState> {
    state: RouteLogsState = {};

    async tabChanged(newTab: string) {
        try {
            this.props.setPageLoading(true);
            switch (newTab) {
                case "Access Logs":
                    await this.props.getAccessLogPage();
                    break;
                case "Daily Organiser Logs":
                    this.props.addInternalSpecification({
                        page: 0,
                        size: 0,
                        total: 0,
                        loading: false,
                        waiting: false,
                        type: "DailyOrganiser",
                        types: [
                            "DOF",
                            "BulletinMessage",
                            "CalendarActivity",
                            "CalendarActivityAdditionalRoom",
                            "CalendarActivityAdditionalTeacher",
                            "CalendarActivityDay",
                            "CalendarActivityRollClass",
                            "CalendarActivityRoom",
                            "CalendarActivityStudent",
                            "CalendarActivityTeacher",
                            "CalendarActivityTeacherRoomLink",
                            "CalendarActivityTimeframe",
                            "CalendarDayEdit",
                            "Calendar",
                            "CancellationReason",
                            "DOSetting",
                            "EmergencyTeacherAvailability",
                            "EmergencyTeacher",
                            "LessonCancellation",
                            "LoadAdjustment",
                            "ReminderMessage",
                            "RoomAvailability",
                            "RoomAvailabilityReason",
                            "RoomEdit",
                            "RoomExtension",
                            "RoomReplacement",
                            "StudentAttendanceChange",
                            "TeacherAbsence",
                            "TeacherAbsenceReason",
                            "TeacherDetails",
                            "TeacherReplacement",
                            "UnsupervisedClass",
                            "YardDutyCancellation",
                        ],
                    });
                    await this.props.getLogPage("DailyOrganiser");
                    break;
                case "Teacher Logs":
                    this.props.addInternalSpecification({
                        page: 0,
                        size: 0,
                        total: 0,
                        loading: false,
                        waiting: false,
                        type: "Teacher",
                        types: ["Teacher"],
                    });
                    await this.props.getLogPage("Teacher");
                    break;
                case "Student Logs":
                    this.props.addInternalSpecification({
                        page: 0,
                        size: 0,
                        total: 0,
                        loading: false,
                        waiting: false,
                        type: "Student",
                        types: ["Student"],
                    });
                    await this.props.getLogPage("Student");
                    break;
                case "User Logs":
                    this.props.addInternalSpecification({
                        page: 0,
                        size: 0,
                        total: 0,
                        loading: false,
                        waiting: false,
                        type: "User",
                        types: ["User"],
                    });
                    await this.props.getLogPage("User");
                    break;
            }
        } finally {
            this.props.setPageLoading(false);
        }
    }

    render() {
        return (
            <section className="full-height">
                <Tabs onTabChange={this.tabChanged.bind(this)}>
                    <TabContainer header="Access Logs">
                        {this.props.accessLogs?.length > 0 ? (
                            <Grid<AccessLog>
                                value={this.props.accessLogs}
                                window={true}
                                fixedHeader={true}
                                fullWidth={true}
                                keyField="id"
                                blockSort={true}
                                defaultSortField="-logged"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                pageSpecification={
                                    this.props.accessLogsPageSpec
                                }
                                onPageChange={
                                    this.props.setAccessLogPageAndSize
                                }>
                                <GridColumn
                                    header="User"
                                    field="user"
                                />
                                <GridColumn
                                    header="IP Address"
                                    field="ipAddress"
                                />
                                <GridColumn
                                    header="Location"
                                    field="location"
                                />
                                <GridColumn
                                    header="Application"
                                    field="application"
                                />
                                <GridColumn
                                    header="Browser"
                                    field="browser"
                                />
                                <GridColumn
                                    header="Source"
                                    field="source"
                                />
                                <GridColumn
                                    header="Status"
                                    field="status"
                                />
                                <GridColumn
                                    header="Logged"
                                    field="logged"
                                    includeTime={true}
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>
                                No access logs found.
                            </Text>
                        )}
                    </TabContainer>
                    <TabContainer header="Daily Organiser Logs">
                        {this.props.logs?.length > 0 ? (
                            <Grid<LogEntry>
                                value={this.props.logs}
                                window={true}
                                fixedHeader={true}
                                fullWidth={true}
                                keyField="id"
                                blockSort={true}
                                defaultSortField="-actionDate"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                pageSpecification={this.props.logsPageSpec}
                                onPageChange={this.props.setLogPageAndSize}>
                                <GridColumn
                                    header="Date Time"
                                    field="actionDate"
                                    includeTime={true}
                                />
                                <GridColumn
                                    header="Description"
                                    field="message"
                                    width="100%"
                                />
                                <GridColumn
                                    header="User"
                                    field="username"
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>No logs found.</Text>
                        )}
                    </TabContainer>
                    <TabContainer header="Teacher Logs">
                        {this.props.logs?.length > 0 ? (
                            <Grid<LogEntry>
                                value={this.props.logs}
                                window={true}
                                fixedHeader={true}
                                fullWidth={true}
                                keyField="id"
                                blockSort={true}
                                defaultSortField="-actionDate"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                pageSpecification={this.props.logsPageSpec}
                                onPageChange={this.props.setLogPageAndSize}>
                                <GridColumn
                                    header="Date Time"
                                    field="actionDate"
                                    includeTime={true}
                                />
                                <GridColumn
                                    header="Description"
                                    field="message"
                                    width="100%"
                                />
                                <GridColumn
                                    header="User"
                                    field="username"
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>No logs found.</Text>
                        )}
                    </TabContainer>
                    <TabContainer header="Student Logs">
                        {this.props.logs?.length > 0 ? (
                            <Grid<LogEntry>
                                value={this.props.logs}
                                window={true}
                                fixedHeader={true}
                                fullWidth={true}
                                keyField="id"
                                blockSort={true}
                                defaultSortField="-actionDate"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                pageSpecification={this.props.logsPageSpec}
                                onPageChange={this.props.setLogPageAndSize}>
                                <GridColumn
                                    header="Date Time"
                                    field="actionDate"
                                    includeTime={true}
                                />
                                <GridColumn
                                    header="Description"
                                    field="message"
                                    width="100%"
                                />
                                <GridColumn
                                    header="User"
                                    field="username"
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>No logs found.</Text>
                        )}
                    </TabContainer>
                    <TabContainer header="User Logs">
                        {this.props.logs?.length > 0 ? (
                            <Grid<LogEntry>
                                value={this.props.logs}
                                window={true}
                                fixedHeader={true}
                                fullWidth={true}
                                keyField="id"
                                blockSort={true}
                                defaultSortField="-actionDate"
                                selectionChanged={selection =>
                                    this.setState({ viewing: selection })
                                }
                                pageSpecification={this.props.logsPageSpec}
                                onPageChange={this.props.setLogPageAndSize}>
                                <GridColumn
                                    header="Date Time"
                                    field="actionDate"
                                    includeTime={true}
                                />
                                <GridColumn
                                    header="Description"
                                    field="message"
                                    width="100%"
                                />
                                <GridColumn
                                    header="User"
                                    field="username"
                                />
                            </Grid>
                        ) : (
                            <Text clearanceSide={Side.All}>No logs found.</Text>
                        )}
                    </TabContainer>
                </Tabs>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { accessLog, logs } = state.db;
    return {
        accessLogs: accessLog.pageData,
        accessLogsPageSpec: accessLog.virtualSpec,
        logs: logs.pageData,
        logsPageSpec: logs.virtualSpec,
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return {
        setPageLoading: (loading: boolean) => dispatch(setPageLoading(loading)),
        getAccessLogPage: () => unwrappedDispatch(getAccessLogPage()),
        setAccessLogPageAndSize: (spec: PageSpecification) =>
            dispatch(setAccessLogPageAndSize(spec)),
        getLogPage: (type: string) => unwrappedDispatch(getLogPage(type)),
        setLogPageAndSize: (specification: LogPageSpecification) =>
            dispatch(setLogPageAndSize(specification)),
        addInternalSpecification: (specification: LogPageSpecification) =>
            dispatch(addInternalSpecification(specification)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteLogs);
